import {
  ClipboardEvent,
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useChatMessage, useMentions } from '@connect-x-app/hybrid-state';
import { MessageMention } from '@connect-x-app/shared-domain-models';
import { escapeHtml, getMentionInProgress, MAX_ALLOWED_MESSAGE_CHARS } from '@connect-x-app/shared-utils';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import classNames from 'classnames';

import { MentionOptions, MentionOptionsRef } from '../mentionOptions/MentionOptions';

import './mentionInput.scss';

type Props = {
  icon: ReactElement;
  message: string;
  sendDisabled: boolean;
  sendMessage: () => void;
  setMessage: (message: string) => void;
};

export type RelevantMentionSection = {
  data: MessageMention[];
  title: string;
};

export type MentionInputRef = {
  sendText: (text: string) => void;
};

// remove forwardRef when migrating to React 19 https://react.dev/reference/react/forwardRef
export const MentionInput = forwardRef<MentionInputRef, Props>(
  ({ icon, message, sendDisabled, sendMessage, setMessage }, ref) => {
    const [mentionInProgress, setMentionInProgress] = useState('');
    const [cursorPositionX, setCursorPositionX] = useState(0);
    const [maxRightPosition, setMaxRightPosition] = useState(0);
    const [cursorIndex, setCursorIndex] = useState(0);
    const [isInputFocused, setIsInputFocused] = useState(true);
    const { t } = useTranslation();
    const { selectMention } = useChatMessage();
    const { canMentionExternally } = useAuthentication();
    const amountOfCharsExceed = useMemo(() => message.length - MAX_ALLOWED_MESSAGE_CHARS, [message.length]);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const suggestionListRef = useRef<MentionOptionsRef>(null);
    const isUpdatingRef = useRef(false);

    useImperativeHandle(ref, () => ({
      sendText: (text: string) => {
        const newMessage = insertText(text);
        const newTextInputIndex = cursorIndex + text.length + 1;
        setTextAndCursorPosition(newMessage, newTextInputIndex);
      },
    }));

    const { filteredExternalMentionOptions, filteredMentionOptions, mentionsRegex } = useMentions(mentionInProgress.trim());

    useLayoutEffect(() => {
      const updateMaxRightPosition = () => {
        setMaxRightPosition(
          (containerRef.current?.getBoundingClientRect().right ?? 0) - (containerRef.current?.getBoundingClientRect().left ?? 0),
        );
      };
      updateMaxRightPosition();

      const observer = new ResizeObserver(() => {
        updateMaxRightPosition();
      });

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    useEffect(() => {
      inputRef.current?.focus();
    }, [inputRef]);

    // AVIO-44078 undo bold external mentions when permission lost and vice versa
    useEffect(() => {
      setTextAndCursorPosition(message, cursorIndex);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canMentionExternally]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
      if (
        suggestionListRef.current &&
        !suggestionListRef.current.containsNode(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsInputFocused(false);
      }
    }, []);

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside]);

    const makeMentionsBold = useCallback(
      (text: string) => {
        const safeText = escapeHtml(text);

        const pattern = new RegExp(mentionsRegex, 'g');
        return safeText.replace(pattern, (match: string) => {
          return `<span class="bold-text">${match}</span>`;
        });
      },
      [mentionsRegex],
    );

    const setTextAndCursorPosition = useCallback(
      (newText: string, cursorIndex: number) => {
        const editor = inputRef.current;
        if (!editor) {
          return;
        }

        isUpdatingRef.current = true;
        editor.textContent = newText;
        editor.innerHTML = makeMentionsBold(newText);
        isUpdatingRef.current = false;

        setMessage(newText.replace(/(\r\n|\n|\r)/gm, ' '));

        restoreCursorPositionAfterTextUpdate(cursorIndex);
        setCursorIndex(cursorIndex);
      },
      [makeMentionsBold, setMessage],
    );

    useEffect(() => {
      const mention = getMentionInProgress(message, mentionsRegex, cursorIndex);
      setMentionInProgress(mention);
      setTextAndCursorPosition(message, cursorIndex);
    }, [message, mentionsRegex, cursorIndex, setTextAndCursorPosition]);

    useEffect(() => {
      // reset inner text value of input field when message is sent
      if (inputRef.current && !message) {
        setTextAndCursorPosition('', 0);
      }
    }, [message, setTextAndCursorPosition]);

    const suggestions: MessageMention[] = useMemo(() => {
      return [...filteredMentionOptions, ...filteredExternalMentionOptions];
    }, [filteredMentionOptions, filteredExternalMentionOptions]);

    const relevantMentions: RelevantMentionSection[] = useMemo(() => {
      return [
        {
          data: filteredMentionOptions,
          title: t('CHAT.MENTIONS.INTERNAL'),
        },
        {
          data: filteredExternalMentionOptions,
          title: t('CHAT.MENTIONS.EXTERNAL'),
        },
      ];
    }, [filteredMentionOptions, t, filteredExternalMentionOptions]);

    const updateCursorIndex = () => {
      const index = getNewCursorPosition() ?? 0;

      setCursorIndex(index);
    };

    const handleEnter = () => {
      if (mentionInProgress && suggestions.length > 0) {
        if (suggestionListRef.current) {
          const currentIndex = suggestionListRef.current.index;
          let correctSuggestionIndex = currentIndex;
          if (currentIndex > suggestions.length - 1) {
            correctSuggestionIndex = 0;
          }
          setMention(suggestions[correctSuggestionIndex]);
        }
        return;
      }

      const canSendMessage = !sendDisabled;
      if (canSendMessage) {
        sendMessage();
      }
    };

    const handleArrowDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionInProgress) {
        event.preventDefault();
        if (suggestionListRef.current) {
          suggestionListRef.current.setIndex((suggestionListRef.current.index + 1) % suggestions.length);
        }
      }
    };

    const handleArrowUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionInProgress) {
        event.preventDefault();
        if (suggestionListRef.current) {
          const currentIndex = suggestionListRef.current.index;
          suggestionListRef.current.setIndex(currentIndex === 0 ? suggestions.length - 1 : currentIndex - 1);
        }
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.code) {
        case 'Enter':
        case 'NumpadEnter':
          event.preventDefault();
          if (!event.shiftKey) {
            handleEnter();
          }
          break;
        case 'ArrowDown':
          handleArrowDown(event);
          break;
        case 'ArrowUp':
          handleArrowUp(event);
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          if (!event.shiftKey) {
            requestAnimationFrame(updateCursorIndex);
          }
          break;
        default:
          break;
      }
    };

    const insertText = useCallback(
      (text: string, from: number = cursorIndex, to: number = cursorIndex) => {
        return `${message.slice(0, from)}${text} ${message.slice(to, message.length)}`;
      },
      [cursorIndex, message],
    );

    const setMention = useCallback(
      (mention: MessageMention) => {
        const currentMention = getMentionInProgress(message, mentionsRegex, cursorIndex);
        const mentionIndex = message.lastIndexOf(currentMention);
        const newMessage = insertText(`@${mention.label}`, mentionIndex, mentionIndex + currentMention.length);

        selectMention(mention);
        // + 2 to take the space after the mention into account and the @ before
        const newTextInputIndex = mentionIndex + mention.label.length + 2;
        setTextAndCursorPosition(newMessage, newTextInputIndex);
      },
      [message, mentionsRegex, cursorIndex, insertText, selectMention, setTextAndCursorPosition],
    );

    const getNewCursorPosition = () => {
      const editor = inputRef.current;
      if (!editor) {
        return null;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return null;
      }

      const range = selection.getRangeAt(0);
      const preRange = range.cloneRange();
      if (inputRef.current) {
        preRange.selectNodeContents(inputRef.current);
      }

      preRange.setEnd(range.startContainer, range.startOffset);
      const cursorOffset = preRange.toString().length;

      return cursorOffset;
    };

    const restoreCursorPositionAfterTextUpdate = (cursorOffset: number | null) => {
      if (cursorOffset === null) {
        return;
      }

      const editor = inputRef.current;
      if (!editor) {
        return;
      }

      let offset = cursorOffset;
      const selection = window.getSelection();
      const range = document.createRange();
      range.setStart(editor, 0);
      range.collapse(true);

      const traverseNodes = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent!.length >= offset) {
            range.setStart(node, offset);
            range.collapse(true);
            return true;
          } else {
            offset -= node.textContent!.length;
          }
        } else {
          for (const child of Array.from(node.childNodes)) {
            if (traverseNodes(child)) {
              return true;
            }
          }
        }
        return false;
      };

      traverseNodes(editor);

      selection?.removeAllRanges();
      selection?.addRange(range);

      if (containerRef.current) {
        const parentRect = containerRef.current.getBoundingClientRect();
        if (range.getBoundingClientRect().left > 0) {
          setCursorPositionX(range.getBoundingClientRect().left - parentRect.left);
        }
      }
    };

    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const plainText = e.clipboardData.getData('text/plain');

        const editor = inputRef.current;
        if (!editor) {
          return;
        }

        const selection = window.getSelection();
        const cursorPos = (() => {
          if (!selection?.rangeCount) {
            return editor.textContent?.length ?? 0;
          }
          const range = selection.getRangeAt(0);
          const preRange = range.cloneRange();
          preRange.selectNodeContents(editor);
          preRange.setEnd(range.startContainer, range.startOffset);
          return preRange.toString().length;
        })();

        const currentText = editor.textContent ?? '';
        const newText = (currentText.slice(0, cursorPos) + plainText + currentText.slice(cursorPos)).replace(
          /(\r\n|\n|\r)/gm,
          ' ',
        );

        setTextAndCursorPosition(newText, cursorPos + plainText.length);
      },
      [setTextAndCursorPosition],
    );

    const handleInput = () => {
      if (isUpdatingRef.current) {
        return;
      }

      const editor = inputRef.current;
      if (!editor) {
        return;
      }

      const cursorIndex = getNewCursorPosition();

      // replace new lines with spaces AVIO-47570
      const newValue = editor.textContent?.replace(/(\r\n|\n|\r)/gm, ' ') ?? '';

      setTextAndCursorPosition(newValue, cursorIndex ?? (newValue.length || 1) - 1);
    };

    return (
      <div className={classNames('input-field', { 'input-field--warning-border': amountOfCharsExceed > 0 })} ref={containerRef}>
        {icon}
        <MentionOptions
          data={relevantMentions}
          maxRightPosition={maxRightPosition}
          onSelect={(mention: MessageMention) => setMention(mention)}
          ref={suggestionListRef}
          startPositionList={cursorPositionX}
          visible={!!mentionInProgress && isInputFocused && !!suggestions.length}
        />
        <div
          className={'message-input'}
          contentEditable="true"
          data-placeholder={t('CHAT.INPUT.PLACEHOLDER')}
          data-test={CHANNEL_TEST_IDS.input.message}
          onClick={updateCursorIndex}
          onFocus={() => setIsInputFocused(true)}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          ref={inputRef}
          role={'presentation'}
        ></div>
      </div>
    );
  },
);
